<script setup lang="ts">
import type { StopNumber } from '@/models/advancedJobBooking';

import KBtn from '@/components/btns/KBtn.vue';

export interface Props {
  fromStop?: StopNumber;
  toStop?: StopNumber;
  showAddFinisherBtn?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fromStop: undefined,
  toStop: undefined,
  showAddFinisherBtn: false,
});

const emit = defineEmits<{
  addBtnClick: [event: Event];
  addFinisherBtnClick: [event: Event];
}>();

function handleAddClick(event: Event): void {
  emit('addBtnClick', event);
}

function handleAddFinisherClick(event: Event): void {
  emit('addFinisherBtnClick', event);
}
</script>

<template>
  <section
    :class="$style.jobBookingProductsSection"
    data-testid="jobBookingProductsSection"
  >
    <header :class="$style.outerHeader">
      <span :class="$style.outerHeaderTitle">
        Products<template v-if="props.fromStop && props.toStop">
          - Stop {{ props.fromStop }} to Stop {{ props.toStop }}</template
        >
      </span>
    </header>
    <div :class="$style.panel">
      <header :class="$style.panelHeader">
        <span :class="$style.panelHeaderTitle">
          <v-icon
            icon="mdi-package-variant-closed"
            size="20"
            :class="$style.panelHeaderIcon"
          />
          Products
        </span>
        <div :class="$style.addBtns">
          <KBtn
            v-if="props.showAddFinisherBtn"
            size="sm"
            variant="text"
            priority="primary"
            :class="$style.addBtn"
            @click="handleAddFinisherClick"
          >
            <v-icon icon="mdi-plus" size="16" :class="$style.addBtnIcon" />
            Add finisher
          </KBtn>
          <KBtn
            size="sm"
            variant="text"
            priority="primary"
            :class="$style.addBtn"
            @click="handleAddClick"
          >
            <v-icon icon="mdi-plus" size="16" :class="$style.addBtnIcon" />
            Add product
          </KBtn>
        </div>
      </header>
      <div :class="$style.rows">
        <slot />
      </div>
    </div>
  </section>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/brand-primary' as bp;

.jobBookingProductsSection {
  display: flex;
  flex-direction: column;
  background-color: colors.$color-white;
  border: 1px solid colors.$color-gray-200;
  border-radius: 8px;
  overflow: hidden;
}
  .outerHeader {
    padding: 12px 16px;
    background-color: bp.$color-brand-50;
    border-bottom: 1px solid colors.$color-gray-200;
  }
  .outerHeaderTitle {
    font-size: 0.875rem;
    font-weight: 600;
    color: colors.$color-text-primary;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .panel {
    display: flex;
    flex-direction: column;
  }
  .panelHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
  }
  .panelHeaderTitle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    color: colors.$color-text-primary;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .panelHeaderIcon {
    color: bp.$color-brand-700;
  }
  .addBtns {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .addBtnIcon {
    margin-right: 4px;
  }
  .rows {
    display: flex;
    flex-direction: column;
  }
</style>
